import React, { useEffect, useMemo, useState } from 'react'
import { useWizard } from '@/common/wizard/wizard'
import { useAppContext } from '@/context/app'
import { FleetUtilizationExports } from './Exports'
import { secondsToDHM } from '@/common/time'
import { TimeDHM } from '@/components/Elements/time'
import { Order, OrderManager } from '@/components/order/Order'
import { StaticPaginatedTable, StaticPaginatedTableState } from '@/components/search/StaticPaginatedTable'
import { WizardData, fleetReportQuery } from './Model'
import { TimeZoneNotice } from '@/components/timezone/TimeZoneNotice'

interface MachineStat {
  id: string; machineId: string; manufacturer: string; category: string; model: string; internalNumber: string
  plannedTime: number; operatorsTime: number; activeTime: number; utilization: number
  activityPeriod: number; activityLoggedIn: number; minMth?: number; maxMth?: number; mthIncrease?: number
}
interface ReportData {
  machines: MachineStat[]
  summary: { periodTime: number; operatorsTime: number; activeTime: number; inactiveTime: number; onFireTime: number; utilization: number; lowUsageCount: number }
}

const orderColumns = ['internalNumber', 'plannedTime', 'operatorsTime', 'utilization', 'activeTime', 'activityPeriod', 'activityLoggedIn', 'minMth', 'maxMth', 'mthIncrease']
const defaultOrder: Order = { key: 'utilization', asc: false }
const scoreKey = (ratio: number) => ratio > .2 ? 'high' : ratio >= .1 ? 'medium' : ratio >= .05 ? 'low' : 'very_low'

export const FleetUtilizationWizardResult = () => {
  const { services, translates } = useAppContext()
  const { wizardData, prevStep } = useWizard<WizardData>()
  const [data, setData] = useState<ReportData>()
  const [pagination, setPagination] = useState<StaticPaginatedTableState>({ pageNumber: 1, pageSize: 5 })
  const [orderManager, setOrderManager] = useState<OrderManager>(() => new OrderManager(orderColumns, defaultOrder, order => {
    setOrderManager(current => current.newManager(order))
    setPagination(current => ({ ...current, pageNumber: 1 }))
  }))

  useEffect(() => {
    services.session.fetchWithToken(`/api/communal/my/reports/fleetutilization?${fleetReportQuery(wizardData)}`)
      .then(response => response.json())
      .then((result: ReportData) => setData({ ...result, machines: (result.machines || []).map(machine => ({ ...machine, id: machine.machineId })) }))
  }, [])

  const orderedMachines = useMemo(() => orderManager.orderListByKey(data?.machines || []), [data, orderManager])
  if (!data || !translates) return <div className='spinner'></div>

  const t = (key: string, fallback?: string) => translates.for(`report.fleetutilization.${key}`, fallback)
  const percentage = (value: number) => data.summary.periodTime > 0 ? `${(value * 100 / data.summary.periodTime).toFixed(1)}%` : '0.0%'
  const loggedPercentage = (value: number) => data.summary.operatorsTime > 0 ? `${(value * 100 / data.summary.operatorsTime).toFixed(1)}%` : '0.0%'
  const openMachine = (machineId: string) => window.open(`${window.location.origin}/communal/statistics/machines?length=1d&machine=${machineId}`, '_blank')
  const header = (key: string, translationKey = key) => <div className='hcol sortable-column' onClick={() => orderManager.click(key)}>
    {t(`table.${translationKey}`)}{orderManager.orderInfoElement(key)}
  </div>

  return <div>
    <h2>{t('summary.title')}</h2>
    <TimeZoneNotice />
    <StaticPaginatedTable<MachineStat>
      data={orderedMachines} parentState={pagination} setParentState={setPagination} translates={translates}
      header={() => <>
        {header('internalNumber', 'machine')}{header('plannedTime', 'planned')}{header('operatorsTime', 'logged')}
        {header('utilization')}{header('activeTime', 'active')}{header('activityPeriod', 'activity_period')}
        {header('activityLoggedIn', 'activity_logged')}{header('minMth', 'mth_start')}{header('maxMth', 'mth_end')}
        {header('mthIncrease', 'mth_increase')}{header('utilization', 'score')}
      </>}
      row={row => <>
        <div className='col fleet-machine-name' onClick={() => openMachine(row.machineId)}>{row.manufacturer} &gt; {row.model} &gt; {row.internalNumber}</div>
        <div className='col' onClick={() => openMachine(row.machineId)}><TimeDHM dhm={secondsToDHM(row.plannedTime)} translates={translates} /></div>
        <div className='col' onClick={() => openMachine(row.machineId)}><TimeDHM dhm={secondsToDHM(row.operatorsTime)} translates={translates} /></div>
        <div className='col' onClick={() => openMachine(row.machineId)}>{(row.utilization * 100).toFixed(1)}%</div>
        <div className='col' onClick={() => openMachine(row.machineId)}><TimeDHM dhm={secondsToDHM(row.activeTime)} translates={translates} /></div>
        <div className='col' onClick={() => openMachine(row.machineId)}>{(row.activityPeriod * 100).toFixed(1)}%</div>
        <div className='col' onClick={() => openMachine(row.machineId)}>{(row.activityLoggedIn * 100).toFixed(1)}%</div>
        <div className='col' onClick={() => openMachine(row.machineId)}>{row.minMth?.toFixed(1) ?? '-'}</div>
        <div className='col' onClick={() => openMachine(row.machineId)}>{row.maxMth?.toFixed(1) ?? '-'}</div>
        <div className='col' onClick={() => openMachine(row.machineId)}>{row.mthIncrease == null ? '-' : `${row.mthIncrease >= 0 ? '+' : ''}${row.mthIncrease.toFixed(1)} h`}</div>
        <div className={`col score-${scoreKey(row.utilization)}`} onClick={() => openMachine(row.machineId)}>● {t(`score.${scoreKey(row.utilization)}`)}</div>
      </>}
    />
    <div className='fleet-summary-section'>
      <h3>{t('summary.section', 'Fleet summary')}</h3>
      <table className='fleet-summary'><tbody>
        {[[t('summary.analysis', 'Total analysis time'), data.summary.periodTime, '100%'], [t('summary.operators', 'Operator logged-in time'), data.summary.operatorsTime, percentage(data.summary.operatorsTime)], [t('summary.active', 'Machine activity time'), data.summary.activeTime, percentage(data.summary.activeTime)], [t('summary.inactive', 'Inactive time'), data.summary.inactiveTime, percentage(data.summary.inactiveTime)], [t('summary.on_fire', 'Utilization while logged in'), data.summary.onFireTime, loggedPercentage(data.summary.onFireTime)]].map(([label, seconds, percent]) => <tr key={String(label)}><td>{label}</td><td><TimeDHM dhm={secondsToDHM(Number(seconds))} translates={translates} /></td><td>{percent}</td></tr>)}
        <tr><td>{t('summary.score', 'Utilization rating')}</td><td colSpan={2} className={`score-${scoreKey(data.summary.utilization)}`}>● {t(`score.${scoreKey(data.summary.utilization)}`)}</td></tr>
      </tbody></table>
      {data.summary.lowUsageCount > 0 && <p className='fleet-warning'>{t('low_usage_warning').replace('%d', String(data.summary.lowUsageCount)).replace('%%', '%')}</p>}
    </div>
    <div className='right-btns right-btns-margin'>
      <button type='button' className='btn btn-secondary' onClick={prevStep}>{translates.for('report.machinesranking.selection.back')}</button>
      <FleetUtilizationExports />
    </div>
  </div>
}
